import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import {
  BatteryMid,
  Calendar,
  Hand,
  HeartHand,
  Lightbulb02,
  Lightning02,
  MessageChatCircle,
  Rocket02,
  Scales02,
  Shield01,
  Sliders04,
  Target05,
  Trophy01,
  Users01,
  Zap,
} from "@untitled-ui/icons-react";
import { ComponentType, SVGProps, useState } from "react";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { classNames } from "@helpers/css";

import healthyData from "./healthy.json";
import mixedData from "./mixed.json";
import strugglingData from "./struggling.json";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Status = "good" | "warn" | "alert" | "critical";
type Priority = "positive" | "low" | "medium" | "high";

interface DashboardCard {
  dimension: string;
  status: Status;
  status_label: string;
  detail: string;
}

interface Action {
  label: string;
  text: string;
  variant: "default" | "green";
}

interface Evidence {
  text: string;
  source_ref: string | null;
}

interface Insight {
  title: string;
  priority: Priority;
  body: string;
  evidence: Evidence[] | null;
  action: Action;
}

interface Dimension {
  name: string;
  status: Status;
  insights: Insight[];
}

interface AgendaItem {
  topic: string;
  tags: string[];
  opener: string;
}

interface PhasedMeeting {
  meeting: string;
  theme: string;
  topics: string[];
}

interface Source {
  type: string;
  title: string;
  source_ref: string;
}

interface DataCounts {
  meetings: number;
  work_summaries: number;
  goals: number;
  checkins: number;
  recognitions: number;
}

interface ReportData {
  meta: {
    manager: { name: string; first_name: string };
    report: { name: string; first_name: string; role: string; tenure: string };
    period: { start: string; end: string };
    generated: string;
    data_counts: DataCounts;
  };
  sources: Source[];
  dashboard: DashboardCard[];
  dimensions: Dimension[];
  agenda: {
    next_meeting: AgendaItem[];
    phased_plan: PhasedMeeting[];
  };
}

type PersonaKey = "healthy" | "mixed" | "struggling";

const PERSONAS: Record<PersonaKey, { label: string; data: ReportData }> = {
  healthy: {
    label: "Dana Rivera → Jordan Okafor",
    data: healthyData as ReportData,
  },
  mixed: {
    label: "Morgan Ellis → Avery Chen",
    data: mixedData as ReportData,
  },
  struggling: {
    label: "Casey Whitmore → Riley Tanaka",
    data: strugglingData as ReportData,
  },
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const statusDot: Record<Status, string> = {
  good: "bg-emerald-500",
  warn: "bg-amber-400",
  alert: "bg-orange-500",
  critical: "bg-red-500",
};

const statusBadge: Record<Status, string> = {
  good: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  warn: "bg-amber-50 text-amber-700 ring-amber-600/20",
  alert: "bg-orange-50 text-orange-700 ring-orange-600/20",
  critical: "bg-red-50 text-red-700 ring-red-600/20",
};

const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const StatusBadge = ({ status, label }: { status: Status; label: string }) => (
  <span
    className={classNames(
      "inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap",
      statusBadge[status],
    )}
  >
    <span
      className={classNames("h-1.5 w-1.5 rounded-full", statusDot[status])}
    />
    {toTitleCase(label)}
  </span>
);

// ---------------------------------------------------------------------------
// Dimension icon mapping
// ---------------------------------------------------------------------------

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const dimensionIcons: Record<string, IconComponent> = {
  "Career Growth": Rocket02,
  "Blockers & Support": Lightning02,
  "Blockers Support": Lightning02,
  "Workload Balance": Scales02,
  "Psychological Safety": Shield01,
  "Feedback & Recognition": Trophy01,
  "Feedback Recognition": Trophy01,
  "Decision Making": Zap,
  "Autonomy & Delegation": Hand,
  "Autonomy Delegation": Hand,
  "Energy Alignment": BatteryMid,
  "Frequency Health": Calendar,
  "Meeting Structure": Sliders04,
  "Talk Ratio": MessageChatCircle,
  "Personal Connection": HeartHand,
  "Expectations Tracker": Target05,
  "Team Dynamics": Users01,
};

const tagIcons: Record<string, IconComponent> = {
  career: Rocket02,
  "career-growth": Rocket02,
  growth: Rocket02,
  mentoring: Rocket02,
  blocker: Lightning02,
  blockers: Lightning02,
  workload: Scales02,
  burnout: Scales02,
  sustainability: Scales02,
  "psychological-safety": Shield01,
  "retention-risk": Shield01,
  feedback: Trophy01,
  recognition: Trophy01,
  visibility: Trophy01,
  decision: Zap,
  "decision-making": Zap,
  "follow-through": Zap,
  autonomy: Hand,
  "energy-alignment": BatteryMid,
  frequency: Calendar,
  "meeting-structure": Sliders04,
  process: Sliders04,
  planning: Sliders04,
  personal: HeartHand,
  "personal-connection": HeartHand,
  rapport: HeartHand,
  relationship: HeartHand,
  "well-being": HeartHand,
  goals: Target05,
  expectations: Target05,
  team: Users01,
  "team-dynamics": Users01,
  project: Lightbulb02,
};

const defaultIcon: IconComponent = Lightbulb02;

const DimensionIcon = ({
  name,
  className,
}: {
  name: string;
  className?: string;
}) => {
  const Icon = dimensionIcons[name] || defaultIcon;
  return <Icon className={className} />;
};

// ---------------------------------------------------------------------------
// Source type labels & icons
// ---------------------------------------------------------------------------

const sourceTypeLabel: Record<string, string> = {
  meeting: "Meeting",
  goal: "Goal",
  recognition: "Recognition",
  checkin: "Check-in",
  work_summary: "Work Summary",
};

const sourceTypeDot: Record<string, string> = {
  meeting: "bg-blue-400",
  goal: "bg-emerald-400",
  recognition: "bg-amber-400",
  checkin: "bg-purple-400",
  work_summary: "bg-gray-400",
};

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const SectionHeading = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <div className="border-b border-gray-200 pb-4">
    <h2 className="text-lg font-semibold leading-7 text-gray-900">{title}</h2>
    {description && (
      <p className="mt-1 text-sm leading-6 text-gray-500">{description}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Dashboard grid — stat cards with icons
// ---------------------------------------------------------------------------

const DashboardGrid = ({ cards }: { cards: DashboardCard[] }) => (
  <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
    {cards.map((card) => (
      <div
        key={card.dimension}
        className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow-sm ring-1 ring-gray-900/5"
      >
        <div className="flex items-center gap-2">
          <DimensionIcon
            name={card.dimension}
            className="h-4 w-4 text-gray-400 shrink-0"
          />
          <dt className="truncate text-sm font-medium text-gray-500">
            {card.dimension}
          </dt>
        </div>
        <dd className="mt-2">
          <StatusBadge status={card.status} label={card.status_label} />
        </dd>
        <dd className="mt-2 text-sm text-gray-500 leading-snug">
          {card.detail}
        </dd>
      </div>
    ))}
  </dl>
);

// ---------------------------------------------------------------------------
// Insight card
// ---------------------------------------------------------------------------

const priorityBadge: Record<Priority, { status: Status; label: string }> = {
  high: { status: "critical", label: "Needs Attention" },
  medium: { status: "alert", label: "Monitor" },
  low: { status: "warn", label: "Low Priority" },
  positive: { status: "good", label: "Doing Well" },
};

const InsightCard = ({ insight }: { insight: Insight }) => {
  const badge = priorityBadge[insight.priority];
  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden flex flex-col">
      <div className="px-5 py-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h4 className="text-base font-semibold text-gray-900">
            {insight.title}
          </h4>
          {badge && <StatusBadge status={badge.status} label={badge.label} />}
        </div>

        <p className="text-sm leading-6 text-gray-600">{insight.body}</p>

        {insight.evidence && insight.evidence.length > 0 && (
          <div className="flex flex-col gap-2">
            {insight.evidence.map((ev, i) => (
              <div key={i} className="border-l-2 border-gray-200 pl-4 py-1">
                <p className="text-sm italic text-gray-500 leading-6">
                  {ev.text}
                  {ev.source_ref && (
                    <button className="not-italic text-xs text-blue-600 hover:text-blue-700 ml-1.5">
                      View{" "}
                      {sourceTypeLabel[
                        ev.source_ref.replace(/_\d+$/, "")
                      ]?.toLowerCase() || "source"}
                    </button>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-50 border-t border-gray-100 px-5 py-4 mt-auto">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
          {insight.action.label}
        </p>
        <p className="text-sm leading-6 text-gray-700">{insight.action.text}</p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dimension section (collapsible)
// ---------------------------------------------------------------------------

const DimensionSection = ({ dimension }: { dimension: Dimension }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <div
        className="flex items-center justify-between gap-3 cursor-pointer group"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5">
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
          )}
          <DimensionIcon
            name={dimension.name}
            className="h-5 w-5 text-gray-500"
          />
          <h3 className="text-base font-semibold leading-7 text-gray-900">
            {dimension.name}
          </h3>
        </div>
        <StatusBadge status={dimension.status} label={dimension.status} />
      </div>
      {isOpen && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {dimension.insights.map((insight) => (
            <InsightCard key={insight.title} insight={insight} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Agenda
// ---------------------------------------------------------------------------

const Tag = ({ tag }: { tag: string }) => {
  const Icon = tagIcons[tag] || defaultIcon;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
      <Icon className="h-3 w-3 text-gray-400" />
      {tag}
    </span>
  );
};

const AgendaSection = ({
  nextMeeting,
  phasedPlan,
}: {
  nextMeeting: AgendaItem[];
  phasedPlan: PhasedMeeting[];
}) => (
  <div className="flex flex-col gap-8">
    {/* Next meeting */}
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 divide-y divide-gray-100">
      <div className="px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">
          Next 1-on-1 Agenda
        </h3>
      </div>
      <div className="px-5">
        <ul className="divide-y divide-gray-100">
          {nextMeeting.map((item, i) => (
            <li key={i} className="flex gap-4 py-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white text-sm font-semibold">
                {i + 1}
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">
                    {item.topic}
                  </span>
                  {item.tags.map((tag) => (
                    <Tag key={tag} tag={tag} />
                  ))}
                </div>
                <p className="text-sm text-gray-500 italic leading-6">
                  {item.opener}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Phased plan */}
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-4">
        Phased Plan — Across 3 Meetings
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {phasedPlan.map((meeting, i) => (
          <div
            key={i}
            className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 p-5 flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white text-sm font-semibold">
                {i + 1}
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {meeting.meeting.replace(/^Meeting \d+ — /, "")}
              </span>
            </div>
            <p className="text-sm text-gray-500 leading-6">{meeting.theme}</p>
            <ul className="flex flex-col gap-2 mt-1">
              {meeting.topics.map((topic) => (
                <li
                  key={topic}
                  className="flex items-start gap-2.5 text-sm text-gray-700 leading-6"
                >
                  <span className="mt-1.5 h-4 w-4 shrink-0 rounded border border-gray-300 bg-white" />
                  {topic}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-2">
              <Button theme={buttonTheme.primary} size={ButtonSize.small}>
                Add agenda to meeting
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Sources section (collapsible, collapsed by default)
// ---------------------------------------------------------------------------

const SourcesSection = ({ sources }: { sources: Source[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  const grouped = sources.reduce<Record<string, Source[]>>((acc, s) => {
    (acc[s.type] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          )}
          <h3 className="text-base font-semibold text-gray-900">Sources</h3>
          <span className="text-sm text-gray-400">({sources.length})</span>
        </div>
      </div>
      {isOpen && (
        <div className="px-5 pb-5 flex flex-col gap-4">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {sourceTypeLabel[type] || type} ({items.length})
              </h4>
              <ul className="flex flex-col gap-1">
                {items.map((source) => (
                  <li
                    key={source.source_ref}
                    className="flex items-center gap-2 text-sm text-gray-600 py-1"
                  >
                    <span
                      className={classNames(
                        "h-2 w-2 rounded-full shrink-0",
                        sourceTypeDot[source.type] || "bg-gray-300",
                      )}
                    />
                    <span>{source.title}</span>
                    <span className="text-xs text-gray-400">
                      {source.source_ref}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Meta stats row
// ---------------------------------------------------------------------------

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const MetaStats = ({
  meta,
  sourceCount,
}: {
  meta: ReportData["meta"];
  sourceCount: number;
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5">
    {[
      { label: "Role", value: meta.report.role },
      { label: "Tenure", value: meta.report.tenure },
      {
        label: "Period",
        value: `${formatDate(meta.period.start)} – ${formatDate(meta.period.end)}`,
      },
      { label: "Sources", value: String(sourceCount) },
    ].map((stat) => (
      <div key={stat.label} className="px-4 py-4 sm:px-5">
        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          {stat.label}
        </dt>
        <dd className="mt-1 text-sm font-semibold text-gray-900">
          {stat.value}
        </dd>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CoachingPage = () => {
  const [persona, setPersona] = useState<PersonaKey>("mixed");
  const { meta, sources, dashboard, dimensions, agenda } =
    PERSONAS[persona].data;

  return (
    <Layout>
      <Layout.Header title="1-on-1 Coaching Report">
        <div className="flex gap-2">
          {(Object.entries(PERSONAS) as [PersonaKey, { label: string }][]).map(
            ([key, { label }]) => (
              <button
                key={key}
                onClick={() => setPersona(key)}
                className={classNames(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  persona === key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 ring-1 ring-inset ring-gray-300 hover:ring-gray-400 bg-white",
                )}
              >
                {label}
              </button>
            ),
          )}
        </div>
      </Layout.Header>
      <Layout.Container>
        <Layout.Main width="lg">
          <div className="flex flex-col gap-12 pb-12">
            {/* Meta stats */}
            <MetaStats meta={meta} sourceCount={sources.length} />

            {/* Dashboard */}
            <div className="flex flex-col gap-5">
              <SectionHeading title="At a Glance" />
              <DashboardGrid cards={dashboard} />
            </div>

            {/* Dimension deep dives */}
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Dimension Deep Dives"
                description="Detailed analysis across each coaching dimension with evidence and suggested actions."
              />
              <div className="flex flex-col gap-10">
                {dimensions.map((dim) => (
                  <DimensionSection key={dim.name} dimension={dim} />
                ))}
              </div>
            </div>

            {/* Agenda */}
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Recommended Agenda"
                description="Prioritized topics for your upcoming conversations."
              />
              <AgendaSection
                nextMeeting={agenda.next_meeting}
                phasedPlan={agenda.phased_plan}
              />
            </div>

            {/* Sources */}
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Data Sources"
                description="All sources used to generate this coaching report."
              />
              <SourcesSection sources={sources} />
            </div>
          </div>
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default CoachingPage;
